import { useQuery } from '@tanstack/react-query';
import { P, match } from 'ts-pattern';

/**
 * Identifies a handful of specific `Content-Type` categories using the `HEAD` method.
 * Missing, or non-matching `Content-Type` headers will default `type` to `null`.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types#discrete_types
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods/HEAD
 */
export function useMediaContentType(src: string) {
  return useQuery({
    queryKey: ['media-content-type', src],
    queryFn: async () => {
      const response = await fetch(src, { method: 'HEAD' });

      if (!response.ok) throw response;
      const contentType = response.headers.get('Content-Type');

      return match(contentType)
        .with(
          P.string.startsWith('image/'),
          () => ({ type: 'image', contentType }) as const,
        )
        .with(
          P.string.startsWith('video/'),
          () => ({ type: 'video', contentType }) as const,
        )
        .with('application/pdf', () => ({ type: 'pdf', contentType }) as const)
        .otherwise(() => ({ type: null, contentType }) as const);
    },
  });
}

export type MediaContentType = NonNullable<
  ReturnType<typeof useMediaContentType>['data']
>['type'];

export type KnownMediaContentType = Exclude<MediaContentType, null>;
